import { Logger } from './logger'

// Tag/attribute allowlist sanitizer for untrusted embed HTML (X (formerly Twitter) oEmbed
// before `innerHTML`). Disallowed tags collapse to a text-only span; on any
// failure it falls back to text content so nothing executable survives.
export class HTMLSanitizer {
  private static allowedTags = [
    'div',
    'span',
    'button',
    'a',
    'strong',
    'em',
    'i',
    'b',
    'small',
    'blockquote',
    'p',
    'br'
  ]
  private static allowedAttributes = ['class', 'href', 'title', 'data-*', 'aria-*', 'role']
  // URL-bearing attrs must use a safe scheme even when the attr NAME is allowed.
  private static SAFE_URL = /^(?:https?:|mailto:|tel:|\/|#)/i

  private static isSafeUrl(value: string): boolean {
    const trimmed = value.trim()
    if (/^\/\//.test(trimmed)) return false
    return this.SAFE_URL.test(trimmed)
  }

  private static isAllowedHrefHost(value: string, allowedHosts: readonly string[]): boolean {
    try {
      const host = new URL(value.trim()).hostname.replace(/^www\./i, '').replace(/^mobile\./i, '')
      return allowedHosts.some((allowed) => host === allowed)
    } catch {
      return false
    }
  }

  static sanitize(html: string, options?: { hrefHosts?: readonly string[] }): string {
    try {
      // Parse inertly: DOMParser does not execute scripts or fire `<img onerror>`
      // / `<svg onload>` handlers, so nothing runs before the scrub. [security: S3/S4]
      const doc = new DOMParser().parseFromString(html, 'text/html')
      this.sanitizeElement(doc.body, options?.hrefHosts)
      return doc.body.innerHTML
    } catch (error) {
      Logger.error('HTML sanitization failed', error)
      const div = document.createElement('div')
      div.textContent = html
      return div.innerHTML
    }
  }

  private static sanitizeElement(element: Element, hrefHosts?: readonly string[]): void {
    const children = Array.from(element.children)

    children.forEach((child) => {
      if (!this.allowedTags.includes(child.tagName.toLowerCase())) {
        const span = document.createElement('span')
        span.textContent = child.textContent || ''
        child.parentNode?.replaceChild(span, child)
        return
      }

      Array.from(child.attributes).forEach((attr) => {
        const name = attr.name.toLowerCase()
        if (!this.isAttributeAllowed(name)) {
          child.removeAttribute(attr.name)
          return
        }
        if (name === 'href') {
          if (!this.isSafeUrl(attr.value)) {
            child.removeAttribute(attr.name)
            return
          }
          if (hrefHosts && !this.isAllowedHrefHost(attr.value, hrefHosts)) {
            child.removeAttribute(attr.name)
          }
        }
      })

      this.sanitizeElement(child, hrefHosts)
    })
  }

  private static isAttributeAllowed(attrName: string): boolean {
    return this.allowedAttributes.some((allowed) => {
      if (allowed.endsWith('*')) {
        return attrName.startsWith(allowed.slice(0, -1))
      }
      return attrName === allowed
    })
  }
}
